/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { cssMap, jsx } from '@compiled/react';

import GlobeIcon from '@atlaskit/icon/core/globe';
import Tile from '@atlaskit/tile/tile';
import { token } from '@atlaskit/tokens';
import {
	getLogoForSourceType,
	getSourceType,
} from '@atlassian/rovo-platform-ui-components/source-logo-stack/source-util';

import type { SuggestionSource } from '../../../aiSuggestionsPluginType';

/** The source types `source-util` knows how to map to a logo. */
type SourceType = ReturnType<typeof getSourceType>;
type KnownSourceType = Exclude<SourceType, 'unknown'>;
type SourceLogo = ReturnType<typeof getLogoForSourceType>;

/** An app a source belongs to. Without a `Logo` the source is unattributed and shows a globe. */
export type SuggestionSourceApp = {
	/** De-duplicates: the source type for Atlassian products, and one shared key for the rest. */
	key: string;
	Logo?: SourceLogo;
	name: string;
};

const SOURCE_APP_NAMES: Record<KnownSourceType, string> = {
	bitbucket: 'Bitbucket',
	confluence: 'Confluence',
	goals: 'Goals',
	home: 'Home',
	jira: 'Jira',
	loom: 'Loom',
	projects: 'Projects',
	teams: 'Teams',
};

/** Shared by every unattributed source so they collapse to a single globe in the app stack. */
const UNATTRIBUTED_KEY = 'unattributed';

/** Hosts whose URLs `getSourceType` may attribute to an Atlassian product. */
const ATLASSIAN_HOST = /(^|\.)(atlassian\.(net|com|cloud)|bitbucket\.org|loom\.com)$/u;

const LEADING_WWW = /^www\./u;

/** Names an unattributed source by its host, which is the most we can say without resolving it. */
const getHostname = (url: string): string | null => {
	try {
		return new URL(url).hostname.replace(LEADING_WWW, '');
	} catch {
		return null;
	}
};

/** The app a source URL belongs to. Only Atlassian products are attributed; the rest show a globe. */
export const getSourceApp = (url: string | null | undefined): SuggestionSourceApp | null => {
	if (!url) {
		return null;
	}

	const hostname = getHostname(url);

	if (!hostname) {
		return null;
	}

	// `getSourceType` matches on substrings of the whole URL, so it reads any `/wiki/` path as
	// Confluence, any `/browse/` as Jira, and so on. Checking the host first keeps a link such as
	// `en.wikipedia.org/wiki/Penguin` from being attributed to an Atlassian product.
	if (ATLASSIAN_HOST.test(hostname)) {
		const sourceType = getSourceType(url);

		if (sourceType !== 'unknown') {
			return {
				key: sourceType,
				Logo: getLogoForSourceType(sourceType),
				name: SOURCE_APP_NAMES[sourceType],
			};
		}
	}

	return { key: UNATTRIBUTED_KEY, name: hostname };
};

/**
 * The apps across a suggestion's sources, in first-seen order. Each Atlassian product appears once,
 * and every unattributed source collapses into a single globe rather than one tile per host.
 */
export const getSuggestionSourceApps = (
	sources: SuggestionSource[] | undefined,
): SuggestionSourceApp[] => {
	// Keyed so each app is kept once. Re-setting a key holds its original position, which is how the
	// globe stays where the first unattributed source appeared once its name is known.
	const apps = new Map<string, SuggestionSourceApp>();
	const unattributedHosts = new Set<string>();

	for (const { url } of sources ?? []) {
		const app = getSourceApp(url);

		if (!app) {
			continue;
		}

		if (!app.Logo) {
			unattributedHosts.add(app.name);
		}

		if (!apps.has(app.key)) {
			apps.set(app.key, app);
		}
	}

	if (unattributedHosts.size > 0) {
		apps.set(UNATTRIBUTED_KEY, {
			key: UNATTRIBUTED_KEY,
			name: [...unattributedHosts].join(', '),
		});
	}

	return [...apps.values()];
};

const styles = cssMap({
	/** The clipping fix: takes the inline SVG off the baseline so a tall line box can't push it out of the slot. */
	logo: {
		display: 'flex',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography -- a typography token would reintroduce the leading this exists to remove
		lineHeight: 0,
	},
});

export type SourceAppIconProps = {
	app: SuggestionSourceApp;
	/** `xsmall` (20px) in the app stack, `xxsmall` (16px) in the Smart Link icon slot. */
	size: 'xxsmall' | 'xsmall';
};

/** A source app's icon: the product logo, or a globe when unattributed. Decorative in both callers. */
export function SourceAppIcon({ app, size }: SourceAppIconProps): React.JSX.Element {
	const { Logo } = app;

	if (Logo) {
		return (
			<span css={styles.logo}>
				<Logo label="" size={size} />
			</span>
		);
	}

	// `Tile` has no `elevation.surface` background, so the tile is transparent over the card body.
	return (
		<Tile
			backgroundColor="transparent"
			hasBorder
			label=""
			size={size}
			testId="ai-suggestion-source-globe"
		>
			<GlobeIcon label="" color={token('color.icon.subtle')} />
		</Tile>
	);
}
